import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
} from '@react-google-maps/api'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { useLoadScripts } from '@lib/google/places/browser'
import { formatDistanceMiles, groupOfferingsByFacility } from './utils'
import OfferingListItem from './offering-list-item'
import { PGA_COACH_SEARCH_MAP_STYLES } from './map-styles'

const MAP_BASE_BACKGROUND = '#f4f5f6'

const GOOGLE_MAP_OPTIONS = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  scaleControl: true,
  clickableIcons: false,
  backgroundColor: MAP_BASE_BACKGROUND,
  styles: PGA_COACH_SEARCH_MAP_STYLES,
}

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '100%',
  minHeight: 420,
}

const DEFAULT_CENTER = {
  lat: 39.5,
  lng: -98.35,
}

/** Fallback maxWidth for Google InfoWindow before panel width is measured; must exceed map column width. */
const MAP_INFO_WINDOW_MAX_WIDTH_FALLBACK_PX = 1280

/** Desktop only: popup list felt too sparse when stretched to full map inset width; keep a readable column instead. */
const DESKTOP_PIN_POPUP_MAX_WIDTH_PX = 380

/** Map root sets this; {@link useStyles} applies it to `.gm-style-iw-*` so pin popups match {@link mobileStackedSheet} width. */
const MAP_POPUP_WIDTH_CSS_VAR = '--pga-coach-map-popup-w'

/** Facility pins + cluster bubbles: unified navy fill; active / multi-pin states use scale & stroke weight only. */
const MAP_PIN_NAVY = '#143d66'

const INFO_WINDOW_CHROME_STYLE_ID = 'pga-coach-search-map-iw-chrome'

/**
 * Hide Google’s default close-row mask only. Keep the speech-bubble tail
 * (`.gm-style-iw-tc`) so the card points at the pin; hide the legacy
 * rotated-square pointer that otherwise hangs off the card.
 * Shadow the card + tail as one silhouette (`filter` on `.gm-style-iw-t`);
 * `box-shadow` on the card alone leaves a disconnected blob on the pointer.
 */
const INFO_WINDOW_CHROME_CSS = `
.pga-coach-search-map .gm-style .gm-style-iw-t::before,
.pga-coach-search-map .gm-style .gm-style-iw-t::after {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
  width: 0 !important;
  height: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  content: none !important;
}
.pga-coach-search-map .gm-style .gm-style-iw-chr,
.pga-coach-search-map .gm-style .gm-ui-hover-effect {
  display: none !important;
}
.pga-coach-search-map .gm-style .gm-style-iw-c {
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  overflow: hidden !important;
  box-shadow: none !important;
}
.pga-coach-search-map .gm-style .gm-style-iw-t {
  filter: drop-shadow(0 12px 28px rgba(15, 34, 52, 0.2));
}
.pga-coach-search-map .gm-style .gm-style-iw-tc,
.pga-coach-search-map .gm-style .gm-style-iw-tc::after {
  filter: none !important;
  box-shadow: none !important;
}
`

function injectInfoWindowChromeStyles() {
  if (typeof document === 'undefined') return undefined
  let style = document.getElementById(INFO_WINDOW_CHROME_STYLE_ID)
  if (!style) {
    style = document.createElement('style')
    style.id = INFO_WINDOW_CHROME_STYLE_ID
    document.head.appendChild(style)
  }
  style.textContent = INFO_WINDOW_CHROME_CSS
  return () => {
    style.remove()
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 420,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#e8ecf1',
    border: '1px solid #e8ecf2',
    boxShadow: 'none',
    '& .gm-style-iw, & .gm-style-iw-a, & .gm-style-iw-t': {
      border: 'none !important',
      boxShadow: 'none !important',
    },
    '& .gm-style-iw-t': {
      filter: 'drop-shadow(0 12px 28px rgba(15, 34, 52, 0.2))',
    },
    '& .gm-style-iw table, & .gm-style-iw td, & .gm-style-iw tr': {
      border: 'none !important',
      background: 'transparent !important',
    },
    '& .gm-style-iw-c': {
      padding: '0 !important',
      border: 'none !important',
      outline: 'none !important',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: 'none',
      boxSizing: 'border-box !important',
      width: `var(${MAP_POPUP_WIDTH_CSS_VAR}, auto) !important`,
      maxWidth: `var(${MAP_POPUP_WIDTH_CSS_VAR}, none) !important`,
      maxHeight: 'min(55vh, 420px) !important',
    },
    '& .gm-style-iw-d': {
      overflowX: 'hidden !important',
      overflowY: 'auto !important',
      maxHeight: 'min(55vh, 420px) !important',
      border: 'none !important',
      boxShadow: 'none !important',
      outline: 'none !important',
      boxSizing: 'border-box !important',
      width: `var(${MAP_POPUP_WIDTH_CSS_VAR}, auto) !important`,
      maxWidth: `var(${MAP_POPUP_WIDTH_CSS_VAR}, none) !important`,
    },
    '& .gm-style-iw-tc': {
      filter: 'none',
    },
  },
  mapSurface: {
    width: '100%',
    height: '100%',
  },
  loadingState: {
    minHeight: 420,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
  },
  overlay: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 2,
    padding: theme.spacing(1, 1.5),
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.12)',
  },
  overlayCompact: {
    top: 'auto',
    left: 'auto',
    bottom: theme.spacing(1.5),
    right: theme.spacing(1.5),
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0 4px 14px rgba(15, 34, 52, 0.08)',
  },
  /** Same `left`/`right` as {@link mobileStackedSheet}; width is measured for pin popups. */
  sheetWidthProbe: {
    position: 'absolute',
    left: theme.spacing(1),
    right: theme.spacing(1),
    bottom: theme.spacing(1.5),
    height: 0,
    margin: 0,
    padding: 0,
    border: 'none',
    overflow: 'hidden',
    visibility: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
    boxSizing: 'border-box',
  },
  /**
   * Fills Google-set popup width (via CSS variable on root); same inset basis as {@link mobileStackedSheet}.
   */
  infoWindowShell: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: '100%',
    padding: 0,
    paddingBottom: theme.spacing(0.5),
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  infoWindowHeader: {
    padding: theme.spacing(1.5, 2, 0.25, 2),
    borderBottom: 'none',
    marginBottom: 0,
    boxSizing: 'border-box',
  },
  mobileSheetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: 0,
  },
  mobileSheetCloseButton: {
    marginTop: 0,
    marginRight: -26,
    color: '#6b7280',
    padding: 4,
  },
  popupCloseButton: {
    color: '#6b7280',
    padding: 4,
    marginTop: 0,
    marginRight: 0,
    flexShrink: 0,
  },
  infoWindowTitle: {
    paddingTop: 0,
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#64758b',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  mobileStackedSheet: {
    position: 'absolute',
    left: theme.spacing(1),
    right: theme.spacing(1),
    bottom: theme.spacing(1.5),
    top: theme.spacing(6),
    zIndex: 3,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #e1e7ef',
    boxShadow: '0 12px 30px rgba(15, 34, 52, 0.24)',
    overflow: 'hidden',
    maxHeight: `calc(100% - ${theme.spacing(7.5)}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  mobileSheetList: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
}))

const getOfferingCoordinates = (offering) => {
  const lat = Number(offering?.coordinates?.lat)
  const lng = Number(offering?.coordinates?.lng)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

const clusterIconUrl = (size, radius) => (
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${radius}" fill="${MAP_PIN_NAVY}" stroke="#ffffff" stroke-width="2"/></svg>`,
  )}`
)

const getMarkerIcon = ({ isActive, hasMultipleOfferings }) => {
  let scale = 0.95
  if (isActive) scale = 1.25
  else if (hasMultipleOfferings) scale = 1.05
  return {
    path: 'M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0',
    fillColor: MAP_PIN_NAVY,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeOpacity: 1,
    strokeWeight: isActive ? 3 : 2,
    scale,
    labelOrigin: { x: 0, y: 0 },
  }
}

const buildViewport = (map) => {
  const bounds = map.getBounds()
  const center = map.getCenter()
  if (!bounds || !center) return null

  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()

  return {
    bounds: {
      north: northEast.lat(),
      east: northEast.lng(),
      south: southWest.lat(),
      west: southWest.lng(),
    },
    center: {
      lat: center.lat(),
      lng: center.lng(),
    },
    zoom: map.getZoom(),
  }
}

const fitMapToOfferings = (map, offerings, initialCenter) => {
  if (!map || typeof window === 'undefined' || !window.google?.maps?.LatLngBounds) return

  const plottable = offerings
    .map((offering) => getOfferingCoordinates(offering))
    .filter(Boolean)

  if (plottable.length === 0) {
    if (initialCenter) {
      map.setCenter(initialCenter)
      map.setZoom(10)
    } else {
      map.setCenter(DEFAULT_CENTER)
      map.setZoom(4)
    }
    return
  }

  if (plottable.length === 1 && !initialCenter) {
    map.setCenter(plottable[0])
    map.setZoom(11)
    return
  }

  const bounds = new window.google.maps.LatLngBounds()
  if (initialCenter) bounds.extend(initialCenter)
  plottable.forEach((coordinates) => bounds.extend(coordinates))
  map.fitBounds(bounds, 80)
}

function PgaCoachSearchMap({
  offerings,
  initialCenter,
  selectedOfferingId,
  pinPopupAnchorOfferingId,
  hoveredOfferingId,
  onSelectOffering,
  onViewportChange,
  onDragEndCenterChange,
  onInteractionStateChange,
  autoFitBounds = true,
  compactOverlay = false,
}) {
  const classes = useStyles()
  const { isLoaded, loadError } = useLoadScripts()
  const mapRef = useRef(null)
  const mapRootRef = useRef(null)
  const sheetWidthProbeRef = useRef(null)
  const lastViewportKeyRef = useRef('')
  const clustererRef = useRef(null)
  const activeFacilityKeysRef = useRef(new Set())
  const markersRef = useRef(new Map())
  const [isDesktop, setIsDesktop] = useState(false)
  const [pinPopupWidthPx, setPinPopupWidthPx] = useState(null)

  const syncPinPopupWidth = useCallback(() => {
    const probe = sheetWidthProbeRef.current
    if (!probe) return
    let w = Math.round(probe.getBoundingClientRect().width)
    if (!Number.isFinite(w) || w <= 0) return
    if (isDesktop) {
      w = Math.min(w, DESKTOP_PIN_POPUP_MAX_WIDTH_PX)
    }
    setPinPopupWidthPx(Math.max(260, w))
  }, [isDesktop])

  const plottablePinsCount = useMemo(
    () => offerings.reduce((n, o) => (getOfferingCoordinates(o) ? n + 1 : n), 0),
    [offerings],
  )
  const facilities = useMemo(() => groupOfferingsByFacility(offerings), [offerings])
  const popupFacility = useMemo(
    () => facilities.find((facility) => facility.offerings.some((offering) => offering.objectID === pinPopupAnchorOfferingId)) || null,
    [facilities, pinPopupAnchorOfferingId],
  )

  useLayoutEffect(() => {
    if (!isLoaded) return
    syncPinPopupWidth()
  }, [isLoaded, syncPinPopupWidth])

  useLayoutEffect(() => {
    if (!isLoaded || !popupFacility) return
    syncPinPopupWidth()
  }, [isLoaded, popupFacility, syncPinPopupWidth])

  useEffect(() => {
    if (!isLoaded) return undefined
    const probe = sheetWidthProbeRef.current
    if (!probe || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => syncPinPopupWidth())
    ro.observe(probe)
    return () => ro.disconnect()
  }, [isLoaded, syncPinPopupWidth])

  useEffect(() => {
    const mq = window.matchMedia('(min-width:960px)')
    const update = () => setIsDesktop(mq.matches)
    update()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update)
      return () => mq.removeEventListener('change', update)
    }
    mq.addListener(update)
    return () => mq.removeListener(update)
  }, [])

  useEffect(() => injectInfoWindowChromeStyles())

  const initialMapCenter = initialCenter || DEFAULT_CENTER
  const initialMapZoom = initialCenter ? 10 : 4

  useEffect(() => {
    if (!autoFitBounds) return
    if (!isLoaded || !mapRef.current) return
    fitMapToOfferings(mapRef.current, offerings, initialCenter)
  }, [autoFitBounds, isLoaded, offerings, initialCenter])

  // Keep active-key ref current, repaint clusters, and imperatively update individual marker icons.
  useEffect(() => {
    const facilityMap = new Map(facilities.map((f) => [f.key, f]))
    activeFacilityKeysRef.current = new Set(
      facilities
        .filter((f) => f.offerings.some((o) => (
          o.objectID === selectedOfferingId || o.objectID === hoveredOfferingId
        )))
        .map((f) => f.key),
    )
    clustererRef.current?.repaint()

    // Purge stale marker refs and update icons for visible individual markers imperatively,
    // since Google Maps markers inside MarkerClusterer may not update from React prop changes alone.
    markersRef.current.forEach((marker, facilityKey) => {
      if (!facilityMap.has(facilityKey)) {
        markersRef.current.delete(facilityKey)
        return
      }
      const facility = facilityMap.get(facilityKey)
      const isActive = activeFacilityKeysRef.current.has(facilityKey)
      marker._offeringCount = facility.offerings.length // eslint-disable-line no-param-reassign
      marker.setIcon(getMarkerIcon({ isActive, hasMultipleOfferings: facility.offerings.length > 1 }))
    })
  }, [selectedOfferingId, hoveredOfferingId, facilities])

  // Returns style index 2 (active) when any marker in the cluster belongs to an active facility.
  const clusterCalculator = useCallback((markers) => {
    const hasActive = markers.some((m) => activeFacilityKeysRef.current.has(m._facilityKey))
    const totalOfferings = markers.reduce((sum, m) => sum + (m._offeringCount || 1), 0)
    return { text: String(totalOfferings), index: hasActive ? 2 : 1 }
  }, [])

  // Only notify parent when bounds/center/zoom actually changed; avoids feedback loops.
  const notifyViewportChange = useCallback(() => {
    if (!mapRef.current || !onViewportChange) return
    const viewport = buildViewport(mapRef.current)
    if (!viewport) return
    const viewportKey = JSON.stringify({
      bounds: viewport.bounds,
      center: viewport.center,
      zoom: viewport.zoom,
    })
    if (viewportKey === lastViewportKeyRef.current) return
    lastViewportKeyRef.current = viewportKey
    onViewportChange(viewport)
  }, [onViewportChange])

  const notifyDragEndCenterChange = useCallback(() => {
    if (!mapRef.current || !onDragEndCenterChange) return
    const viewport = buildViewport(mapRef.current)
    if (!viewport?.center) return
    onDragEndCenterChange(viewport.center)
  }, [onDragEndCenterChange])

  if (loadError) {
    return (
      <div className={`${classes.root} pga-coach-search-map`}>
        <div className={classes.loadingState}>We could not load the map right now.</div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className={`${classes.root} pga-coach-search-map`}>
        <div className={classes.loadingState}>Loading map...</div>
      </div>
    )
  }

  return (
    <div
      className={`${classes.root} pga-coach-search-map`}
      ref={mapRootRef}
      style={
        pinPopupWidthPx != null
          ? { [MAP_POPUP_WIDTH_CSS_VAR]: `${pinPopupWidthPx}px` }
          : undefined
      }
    >
      <div ref={sheetWidthProbeRef} className={classes.sheetWidthProbe} aria-hidden />
      <div className={`${classes.overlay} ${compactOverlay ? classes.overlayCompact : ''}`.trim()}>
        <Typography variant={compactOverlay ? 'caption' : 'body2'} color="textSecondary">
          {plottablePinsCount === offerings.length
            ? `${offerings.length} pin${offerings.length === 1 ? '' : 's'}`
            : `${plottablePinsCount} of ${offerings.length} on map`}
        </Typography>
      </div>
      <div className={classes.mapSurface}>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          defaultCenter={initialMapCenter}
          defaultZoom={initialMapZoom}
          options={GOOGLE_MAP_OPTIONS}
          onLoad={(map) => {
            mapRef.current = map
            fitMapToOfferings(map, offerings, initialCenter)
            notifyViewportChange()
          }}
          onUnmount={() => {
            mapRef.current = null
          }}
          onDragStart={() => onInteractionStateChange?.(true)}
          onDragEnd={notifyDragEndCenterChange}
          onZoomChanged={() => onInteractionStateChange?.(true)}
          onIdle={() => {
            notifyViewportChange()
            onInteractionStateChange?.(false)
          }}
          onClick={() => onSelectOffering?.(null)}
        >
          <MarkerClusterer
            averageCenter
            enableRetinaIcons
            gridSize={52}
            calculator={clusterCalculator}
            styles={[
              {
                url: clusterIconUrl(32, 13),
                height: 32,
                width: 32,
                textColor: '#ffffff',
                textSize: 11,
                fontWeight: '700',
                anchorText: [0, 0],
              },
              {
                url: clusterIconUrl(42, 16),
                height: 42,
                width: 42,
                textColor: '#ffffff',
                textSize: 13,
                fontWeight: '700',
                anchorText: [0, 0],
              },
            ]}
            onLoad={(clusterer) => { clustererRef.current = clusterer }}
          >
            {(clusterer) => facilities.map((facility) => {
              const isActive = facility.offerings.some((offering) => (
                offering.objectID === selectedOfferingId || offering.objectID === hoveredOfferingId
              ))
              const firstOffering = facility.offerings[0]

              return (
                <Marker
                  key={facility.key}
                  clusterer={clusterer}
                  position={facility.position}
                  onLoad={(marker) => {
                    marker._facilityKey = facility.key // eslint-disable-line no-param-reassign
                    marker._offeringCount = facility.offerings.length // eslint-disable-line no-param-reassign
                    markersRef.current.set(facility.key, marker)
                  }}
                  onClick={() => onSelectOffering?.(firstOffering?.objectID || null, {
                    source: 'marker',
                    offeringIds: facility.offerings.map((offering) => offering.objectID),
                    facilityKey: facility.key,
                  })}
                  icon={getMarkerIcon({
                    isActive,
                    hasMultipleOfferings: facility.offerings.length > 1,
                  })}
                  label={facility.offerings.length > 1 ? {
                    text: String(facility.offerings.length),
                    color: '#ffffff',
                    fontSize: isActive ? '13px' : '11px',
                    fontWeight: '700',
                  } : undefined}
                />
              )
            })}
          </MarkerClusterer>
          {popupFacility && (isDesktop || popupFacility.offerings.length === 1) && (
            <InfoWindow
              position={popupFacility.position}
              onCloseClick={() => onSelectOffering?.(null)}
              onLoad={(infoWindow) => {
                if (typeof window === 'undefined' || !window.google?.maps?.event || !infoWindow) return
                window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                  const closeBtn = document.querySelector(
                    '.pga-coach-search-map button[aria-label="Close offerings panel"]',
                  )
                  closeBtn?.blur()
                })
              }}
              options={{
                maxWidth: pinPopupWidthPx ?? MAP_INFO_WINDOW_MAX_WIDTH_FALLBACK_PX,
                headerDisabled: true,
              }}
            >
              <div className={classes.infoWindowShell}>
                <div className={classes.infoWindowHeader}>
                  <div className={classes.mobileSheetHeader}>
                    <Typography className={classes.infoWindowTitle}>
                      {popupFacility.venueName}
                    </Typography>
                    <IconButton
                      size="small"
                      disableRipple
                      disableFocusRipple
                      autoFocus={false}
                      aria-label="Close offerings panel"
                      className={classes.popupCloseButton}
                      onClick={() => onSelectOffering?.(null)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
                {popupFacility.offerings.map((offering, index) => {
                  const distanceLabel = formatDistanceMiles(offering.geoDistanceMeters)
                  return (
                    <OfferingListItem
                      key={offering.objectID}
                      offering={offering}
                      facilityName={popupFacility.venueName}
                      facilityKey={popupFacility.key}
                      position={index + 1}
                      onSelectOffering={onSelectOffering}
                      distanceLabel={distanceLabel}
                    />
                  )
                })}
              </div>
            </InfoWindow>
          )}

        </GoogleMap>
      </div>
      {popupFacility && !isDesktop && popupFacility.offerings.length > 1 && (
        <div className={classes.mobileStackedSheet}>
          <div className={classes.infoWindowHeader}>
            <div className={classes.mobileSheetHeader}>
              <Typography className={classes.infoWindowTitle}>
                {popupFacility.venueName}
              </Typography>
              <IconButton
                size="small"
                aria-label="Close offerings panel"
                className={classes.mobileSheetCloseButton}
                onClick={() => onSelectOffering?.(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div className={classes.mobileSheetList}>
            {popupFacility.offerings.map((offering, index) => {
              const distanceLabel = formatDistanceMiles(offering.geoDistanceMeters)
              return (
                <OfferingListItem
                  key={offering.objectID}
                  offering={offering}
                  facilityName={popupFacility.venueName}
                  facilityKey={popupFacility.key}
                  isMobileSheet
                  position={index + 1}
                  onSelectOffering={onSelectOffering}
                  distanceLabel={distanceLabel}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

PgaCoachSearchMap.propTypes = {
  offerings: PropTypes.arrayOf(PropTypes.shape({
    objectID: PropTypes.string,
    title: PropTypes.string,
    venueName: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    priceInCents: PropTypes.number,
    priceLabel: PropTypes.string,
    coordinates: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  })),
  initialCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  selectedOfferingId: PropTypes.string,
  pinPopupAnchorOfferingId: PropTypes.string,
  hoveredOfferingId: PropTypes.string,
  onSelectOffering: PropTypes.func,
  onViewportChange: PropTypes.func,
  onDragEndCenterChange: PropTypes.func,
  onInteractionStateChange: PropTypes.func,
  autoFitBounds: PropTypes.bool,
  compactOverlay: PropTypes.bool,
}

export default PgaCoachSearchMap
